import {
    Box,
    Typography,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Card,
    CardContent,
    Divider,
    Chip,
    IconButton,
    Tooltip,
    Stack,
    CircularProgress,
    Alert,
    TextField,
    InputAdornment,
} from "@mui/material";
import { useEffect, useState } from "react";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import InventoryIcon from "@mui/icons-material/Inventory";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";

import {
    inventoryManagementUrl,
    salesManagement,
    companyManagementUrl,
} from "../../../config";
import DynamicTable from "../../../data/DynamicTable/dynamicTable";
import {
    fetchDataEngine,
    graphqlQuery,
} from "../../../data/Axios/DynamicService";
import {
    allwarehouseinventory,
    allwarehouseinventoryview,

    allwarehouseinventorybyid,
    allCompanyBranchesView,
    SearchWarehouseInventory,
    GetWarehouseInventoryStats,
} from "../../../data/Axios/queries";
import { format } from "date-fns";

const base_url = inventoryManagementUrl.uri;

const actions = {
    entity: "Warehouse Inventory",
    edit: {
        key: "EDIT",
        button_name: "Edit",
        Show_Button: false,
        permission: "EditPermission",
    },
    add: {
        key: "ADD",
        button_name: "Add",
        Show_Button: false,
        permission: "CreatePermission",
    },
    activateDeactivate: {
        key: "ACTIVATEDEACTIVATE",
        button_name: "Deactivate",
        Show_Button: true,
        permission: "ActivatePermission",
        modelName: "inventoryIds",
        inputObjectName: "bulkActivateDeactivateInventory",
    },
    AssignTask: {
        key: "ASSIGN_TASK",
        button_name: "Assign Task",
        Show_Button: false,
        permission: "hasNoPermission",
        roleGroupId: [1, 2],
    },
    ShowDistributionDetails: {
        key: "SHOW_DISTRIBUTION_DETAILS",
        button_name: "View Distribution",
        Show_Button: true,
        permission: "hasNoPermission",
        roleGroupId: [1, 2, 3, 4],
    },
    showMore: {
        key: "SHOWMORE",
        button_name: "Show More",
        Show_Button: true,
        permission: "hasNoPermission",
        roleGroupId: [1, 2, 3, 4],
        title: "Warehouse Inventory Details",
    },
};

function WarehouseInventoryTable() {
    const [warehouseInventory, setWarehouseInventory] = useState([]);
    const [totalAmount, setTotalAmount] = useState(0);
    const [branches, setBranches] = useState([]);
    const [selectedBranch, setSelectedBranch] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // Statistics
    const [stats, setStats] = useState({
        totalCartons: 0,
        totalItems: 0,
        expectedProfit: 0,
        activeProducts: 0,
    });

    const [currentQuery, setCurrentQuery] = useState(allwarehouseinventoryview);
    const [currentBaseUrl, setCurrentBaseUrl] = useState(
        inventoryManagementUrl.uri
    );
    const [variables, setVariables] = useState({});
    const [searchQuery, setSearchQuery] = useState("");
    const [isSearching, setIsSearching] = useState(false);

    // Pagination state
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [rowCount, setRowCount] = useState(0);

    // Theme colors
    const theme = {
        primary: "#1976D2",
        secondary: "#0288D1",
        success: "#4CAF50",
        warning: "#FF9800",
        error: "#F44336",
        lightBg: "#E3F2FD",
        cardBg: "#FFFFFF",
    };

    // ✅ Fetch branches for dropdown
    const fetchBranches = async () => {
        try {
            const payload = { isWarehouse: 1 };
            const returnFields = ["id", "branchName"];
            const res = await fetchDataEngine(
                "allBranchesByColumnValues",
                "branchModel",
                payload,
                returnFields,
                companyManagementUrl.uri
            );
            setBranches(res || []);
        } catch (error) {
            console.error("Error fetching branches:", error);
            setError("Failed to load branches");
        }
    };

    // ✅ Calculate statistics (client-side fallback - only used if server stats fail)
    const calculateStats = (data) => {
        const totalCartons = data.reduce((sum, item) => sum + (item.cartons || 0), 0);
        const totalItems = data.reduce(
            (sum, item) => sum + (item.cartons || 0) * (item.quantityPerCarton || 0),
            0
        );
        const expectedProfit = data.reduce(
            (sum, item) => sum + (item.expectedProfit || 0),
            0
        );
        const activeProducts = data.filter((item) => item.isActive).length;

        setStats({
            totalCartons,
            totalItems,
            expectedProfit,
            activeProducts,
        });
    };

    // Fetch stats from server (for accurate banner totals)
    const fetchStats = async () => {
        try {
            const query = GetWarehouseInventoryStats(selectedBranch || 0);
            const result = await graphqlQuery(query, base_url);

            // Handle array response (like warehouse distribution fix)
            const statsArray = Array.isArray(result)
                ? result
                : result?.warehouseInventoryStats || [];

            if (statsArray && statsArray.length > 0) {
                const statsData = statsArray[0];
                setStats({
                    totalCartons: statsData.totalCartons || 0,
                    totalItems: statsData.totalItems || 0,
                    expectedProfit: statsData.totalExpectedProfit || 0,
                    activeProducts: statsData.activeProducts || 0,
                });
                // Also update totalAmount from server stats
                const formattedTotal = (statsData.totalValue || 0).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                });
                setTotalAmount(formattedTotal);
            }
        } catch (error) {
            console.error("Error fetching stats:", error);
            // Set fallback values
            setStats({
                totalCartons: 0,
                totalItems: 0,
                expectedProfit: 0,
                activeProducts: 0,
            });
            setTotalAmount("0.00");
        }
    };

    // Fetch data for inventory
    const fetchWarehouseInventory = async () => {
        try {
            setLoading(true);
            setError("");

            // Use SearchWarehouseInventory with server-side pagination
            const query = SearchWarehouseInventory({
                branchId: selectedBranch,
                search: searchQuery || "",
                limit: pageSize,
                pageNumber: page + 1, // MUI is 0-based, backend is 1-based
            });

            const result = await graphqlQuery(query, base_url);

            // graphqlQuery auto-unwraps: result IS the Connection object { items: [...], totalCount: N }
            const connection = result || {};
            const rows = connection.items || (Array.isArray(result) ? result : []);
            const totalCount = connection.totalCount || rows.length;

            if (rows && rows.length > 0) {
                // FIX 4: Keep raw timestamps — do NOT pre-format to date-only.
                // The dynamicTable renderCell and showMore dialog both apply Africa/Nairobi TZ correctly.
                // Pre-formatting here was stripping the time component and causing incorrect timestamps.
                const formatted = rows.map((item) => ({ ...item }));

                setWarehouseInventory(formatted);
                setRowCount(totalCount);

                const total = formatted.reduce(
                    (sum, item) => sum + (item.totalValue || 0),
                    0
                );

                const formattedTotal = total.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                });

                setTotalAmount(formattedTotal);
            } else {
                // Clear state if no data
                setWarehouseInventory([]);
                setRowCount(0);
                setTotalAmount("0.00");
            }
        } catch (error) {
            console.error("Error fetching warehouse inventory:", error);
            setError("Failed to load inventory data. Please try again.");
            setWarehouseInventory([]);
            setRowCount(0);
            setTotalAmount("0.00");
        } finally {
            setLoading(false);
        }
    };

    // Handle refresh
    const handleRefresh = () => {
        fetchWarehouseInventory();
    };

    // ✅ Load data on mount
    useEffect(() => {
        fetchBranches();
        fetchStats();
    }, []);

    // ✅ Re-fetch stats when branch changes
    useEffect(() => {
        fetchStats();
    }, [selectedBranch]);

    // ✅ Reset page to 0 when filters change
    useEffect(() => {
        setPage(0);
    }, [selectedBranch, searchQuery]);
//deployment test
    // ✅ Re-fetch when branch, search query, search limit, or page changes
    useEffect(() => {
        const handler = setTimeout(() => {
            fetchWarehouseInventory();
        }, 500);

        return () => clearTimeout(handler);
    }, [selectedBranch, searchQuery, page, pageSize]);

    return (
        <Box sx={{ p: 3, bgcolor: "#F5F7FA", minHeight: "100vh" }}>
            {/* Header Section */}
            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <WarehouseIcon sx={{ fontSize: 32, color: theme.primary, mr: 1 }} />
                    <Typography variant="h4" fontWeight="700" color={theme.primary}>
                        Warehouse Inventory Management
                    </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                    Monitor and manage warehouse inventory across all branches
                </Typography>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
                    {error}
                </Alert>
            )}

            {/* Statistics Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                {/* Total Value Card */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card
                        elevation={0}
                        sx={{
                            background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.secondary} 100%)`,
                            color: "white",
                            position: "relative",
                            overflow: "hidden",
                            "&::before": {
                                content: '""',
                                position: "absolute",
                                width: "150px",
                                height: "150px",
                                background: "rgba(255, 255, 255, 0.1)",
                                borderRadius: "50%",
                                top: "-50px",
                                right: "-50px",
                            },
                        }}
                    >
                        <CardContent>
                            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                                <AttachMoneyIcon sx={{ fontSize: 28, mr: 1 }} />
                                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                    Total Value
                                </Typography>
                            </Box>
                            <Typography variant="h4" fontWeight="700">
                                KSh {totalAmount}
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.8, mt: 1 }}>
                                {selectedBranch
                                    ? branches.find((b) => b.id === selectedBranch)?.branchName
                                    : "All Branches"}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Total Cartons Card */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card elevation={0} sx={{ bgcolor: theme.cardBg, height: "100%" }}>
                        <CardContent>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-start",
                                }}
                            >
                                <Box>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        fontWeight="600"
                                    >
                                        Total Cartons
                                    </Typography>
                                    <Typography variant="h4" fontWeight="700" color={theme.primary}>
                                        {stats.totalCartons.toLocaleString()}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {stats.totalItems.toLocaleString()} pairs
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        bgcolor: theme.lightBg,
                                        p: 1.5,
                                        borderRadius: 2,
                                    }}
                                >
                                    <InventoryIcon sx={{ color: theme.primary, fontSize: 28 }} />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Expected Profit Card */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card elevation={0} sx={{ bgcolor: theme.cardBg, height: "100%" }}>
                        <CardContent>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-start",
                                }}
                            >
                                <Box>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        fontWeight="600"
                                    >
                                        Expected Profit
                                    </Typography>
                                    <Typography variant="h4" fontWeight="700" color={theme.success}>
                                        KSh{" "}
                                        {stats.expectedProfit.toLocaleString(undefined, {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                        })}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Projected earnings
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        bgcolor: "#E8F5E9",
                                        p: 1.5,
                                        borderRadius: 2,
                                    }}
                                >
                                    <TrendingUpIcon sx={{ color: theme.success, fontSize: 28 }} />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Active Products Card */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card elevation={0} sx={{ bgcolor: theme.cardBg, height: "100%" }}>
                        <CardContent>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-start",
                                }}
                            >
                                <Box>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        fontWeight="600"
                                    >
                                        Active Products
                                    </Typography>
                                    <Typography variant="h4" fontWeight="700" color={theme.warning}>
                                        {stats.activeProducts}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        of {warehouseInventory.length} total
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        bgcolor: "#FFF3E0",
                                        p: 1.5,
                                        borderRadius: 2,
                                    }}
                                >
                                    <WarehouseIcon sx={{ color: theme.warning, fontSize: 28 }} />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Filter and Actions Section */}
            <Paper
                elevation={0}
                sx={{
                    p: 2.5,
                    mb: 3,
                    borderRadius: 2,
                    bgcolor: theme.cardBg,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 2,
                    }}
                >
                    {/* Left side - Filter */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            flex: 1,
                            flexWrap: "wrap",
                        }}
                    >
                        <TextField
                            sx={{ minWidth: 300 }}
                            size="small"
                            placeholder="Search by product (name, code)..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: "text.secondary" }} />
                                    </InputAdornment>
                                ),
                                endAdornment: searchQuery && (
                                    <InputAdornment position="end">
                                        {isSearching ? (
                                            <CircularProgress size={20} />
                                        ) : (
                                            <IconButton
                                                size="small"
                                                onClick={() => setSearchQuery("")}
                                            >
                                                <RefreshIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <FilterListIcon sx={{ color: theme.primary }} />
                        <FormControl sx={{ minWidth: 280 }} size="small">
                            <InputLabel>Filter by Warehouse</InputLabel>
                            <Select
                                value={selectedBranch}
                                label="Filter by Warehouse"
                                onChange={(e) => setSelectedBranch(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>All Warehouses</em>
                                </MenuItem>
                                {branches.map((branch) => (
                                    <MenuItem key={branch.id} value={branch.id}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <WarehouseIcon sx={{ fontSize: 18, color: theme.primary }} />
                                            {branch.branchName}
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {selectedBranch && (
                            <Chip
                                label="Filtered"
                                size="small"
                                color="primary"
                                onDelete={() => setSelectedBranch("")}
                            />
                        )}

                        {searchQuery && (
                            <Chip
                                label={`Search: ${searchQuery}`}
                                size="small"
                                color="primary"
                                onDelete={() => setSearchQuery("")}
                            />
                        )}
                    </Box>

                    {/* Right side - Actions */}
                    <Stack direction="row" spacing={1}>
                        <Tooltip title="Refresh data">
                            <IconButton
                                onClick={handleRefresh}
                                disabled={loading}
                                sx={{
                                    bgcolor: theme.lightBg,
                                    "&:hover": { bgcolor: theme.primary, color: "white" },
                                }}
                            >
                                {loading ? (
                                    <CircularProgress size={20} />
                                ) : (
                                    <RefreshIcon />
                                )}
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Box>
            </Paper>

            {/* Dynamic Table */}
            <Paper
                elevation={0}
                sx={{
                    borderRadius: 2,
                    overflow: "hidden",
                    bgcolor: theme.cardBg,
                }}
            >
                {loading && warehouseInventory.length === 0 ? (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            minHeight: 400,
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : (
                    <DynamicTable
                        title="Inventory Items"
                        subtitle={`Showing ${warehouseInventory.length} of ${rowCount} items ${selectedBranch
                            ? `from ${branches.find((b) => b.id === selectedBranch)?.branchName ||
                            "selected warehouse"
                            }`
                            : "from all warehouses"
                            }`}
                        query={allwarehouseinventoryview}
                        base_url={currentBaseUrl}
                        actions={actions}
                        rows={warehouseInventory}
                        rowCount={rowCount}
                        page={page}
                        pageSize={pageSize}
                        onPageChange={(newPage) => setPage(newPage)}
                        onPageSizeChange={(newPageSize) => {
                            setPageSize(newPageSize);
                            setPage(0);
                        }}
                        paginationMode="server"
                        pagination={true}
                    />
                )}
            </Paper>
        </Box>
    );
}

export default WarehouseInventoryTable;